import { useEffect, useMemo, useState } from "react";
import { motion, useIsPresent, useReducedMotion } from "framer-motion";
import { computeCollageLayout } from "./collageLayout";
import { defaultAspect, getCachedAspect } from "./usePhotoMonths";

const monthWords = ["one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];

export const monthLabel = (monthNumber) => `month ${monthWords[monthNumber - 1] || monthNumber}`;

export const entranceStyles = ["burst", "rain", "spiral", "heart"];

const collageArea = ({ width, height, insets }) => {
  const isShort = height < 560;
  const top = insets.top + (isShort ? 50 : 96);
  const bottom = insets.bottom + (isShort ? 64 : 100);
  const side = Math.max(14, width * 0.035);
  return {
    left: insets.left + side,
    top,
    width: Math.max(0, width - insets.left - insets.right - side * 2),
    height: Math.max(0, height - top - bottom),
  };
};

const entranceSeconds = {
  burst: (count) => 0.15 + count * 0.05 + 1.45,
  rain: (count) => count * 0.06 + 0.45 + 2.35,
  spiral: (count) => 0.1 + count * 0.07 + 1.65,
  heart: (count) => 0.1 + count * 0.05 + 2.7,
};

const interactiveDelayMilliseconds = 900;

const easeOutSoft = [0.16, 0.84, 0.3, 1];

const heartPoint = (step) => {
  const angle = step * Math.PI * 2;
  return {
    x: 16 * Math.pow(Math.sin(angle), 3),
    y: -(13 * Math.cos(angle) - 5 * Math.cos(2 * angle) - 2 * Math.cos(3 * angle) - Math.cos(4 * angle)) - 2.5,
  };
};

const buildEntrance = ({ style, card, index, count, origin, area, viewport, pose }) => {
  const halfWidth = card.width / 2;
  const halfHeight = card.height / 2;
  const originX = origin.x - halfWidth;
  const originY = origin.y - halfHeight;

  if (style === "rain") {
    const sway = (30 + card.drift * 50) * (index % 2 ? 1 : -1);
    const startY = -card.height - 40 - card.drift * viewport.height * 0.35;
    const duration = 1.75 + card.drift * 0.6;
    const delay = index * 0.06 + card.delayJitter * 0.45;
    const swayTimes = { duration, delay, ease: "easeInOut", times: [0, 0.4, 0.75, 1] };
    return {
      initial: { x: pose.x + sway, y: startY, rotate: pose.rotate + card.spin * 160, scale: pose.scale * 0.94, opacity: 1 },
      animate: {
        x: [pose.x + sway, pose.x - sway * 0.55, pose.x + sway * 0.22, pose.x],
        y: pose.y,
        rotate: [pose.rotate + card.spin * 160, pose.rotate - card.spin * 70, pose.rotate + card.spin * 24, pose.rotate],
        scale: pose.scale,
        opacity: 1,
      },
      transition: {
        x: swayTimes,
        rotate: swayTimes,
        y: { duration, delay, ease: [0.3, 0.2, 0.25, 1] },
        scale: { duration, delay },
      },
    };
  }

  if (style === "spiral") {
    const deltaX = pose.x + halfWidth - origin.x;
    const deltaY = pose.y + halfHeight - origin.y;
    const finalRadius = Math.hypot(deltaX, deltaY);
    const finalAngle = Math.atan2(deltaY, deltaX);
    const steps = [0, 0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 0.875, 1];
    const sweep = Math.PI * 1.35;
    const scales = steps.map((step) => 0.12 + (pose.scale - 0.12) * Math.pow(step, 0.8));
    const reachRadius = Math.hypot(halfWidth, halfHeight);
    const keepInside = (center, reach, limit) => {
      const lowest = 6 + reach;
      const highest = limit - 6 - reach;
      return lowest > highest ? limit / 2 : Math.min(highest, Math.max(lowest, center));
    };
    const pathX = steps.map((step, position) => {
      const centerX = origin.x + Math.cos(finalAngle - (1 - step) * sweep) * finalRadius * Math.pow(step, 0.75);
      return keepInside(centerX, reachRadius * scales[position], viewport.width) - halfWidth;
    });
    const pathY = steps.map((step, position) => {
      const centerY = origin.y + Math.sin(finalAngle - (1 - step) * sweep) * finalRadius * Math.pow(step, 0.75);
      return keepInside(centerY, reachRadius * scales[position], viewport.height) - halfHeight;
    });
    pathX[pathX.length - 1] = pose.x;
    pathY[pathY.length - 1] = pose.y;
    const pathTransition = {
      duration: 1.5,
      delay: 0.1 + index * 0.07,
      ease: "linear",
      times: steps.map((step) => Math.pow(step, 1.25)),
    };
    return {
      initial: { x: originX, y: originY, rotate: pose.rotate - 200, scale: 0.12, opacity: 0 },
      animate: {
        x: pathX,
        y: pathY,
        rotate: steps.map((step) => pose.rotate - 200 * (1 - step)),
        scale: scales,
        opacity: steps.map((step) => (step === 0 ? 0 : 1)),
      },
      transition: { x: pathTransition, y: pathTransition, rotate: pathTransition, scale: pathTransition, opacity: pathTransition },
    };
  }

  if (style === "heart") {
    const point = heartPoint(index / count);
    const heartRadius = Math.min(area.width, area.height) * 0.4;
    const heartCenterX = area.left + area.width / 2;
    const heartCenterY = area.top + area.height / 2;
    const heartScale = Math.min(0.5, (heartRadius * 0.55) / Math.max(card.width, card.height) + 0.12);
    const heartX = heartCenterX + (point.x / 16) * heartRadius - halfWidth;
    const heartY = heartCenterY + (point.y / 16) * heartRadius - halfHeight;
    const heartTilt = (card.spin - 0.5) * 8;
    const heartTransition = {
      duration: 2.6,
      delay: 0.1 + index * 0.05,
      times: [0, 0.36, 0.6, 1],
      ease: ["easeOut", "easeInOut", easeOutSoft],
    };
    return {
      initial: { x: originX, y: originY, rotate: 0, scale: 0.08, opacity: 0 },
      animate: {
        x: [originX, heartX, heartX, pose.x],
        y: [originY, heartY, heartY, pose.y],
        rotate: [0, heartTilt, heartTilt, pose.rotate],
        scale: [0.08, heartScale, heartScale * 1.08, pose.scale],
        opacity: [0, 1, 1, 1],
      },
      transition: { x: heartTransition, y: heartTransition, rotate: heartTransition, scale: heartTransition, opacity: heartTransition },
    };
  }

  const apexY = Math.max(12 - halfHeight * 0.1, Math.min(originY, pose.y) - (70 + card.drift * 130));
  const midX = originX + (pose.x - originX) * 0.55;
  const delay = 0.15 + index * 0.05;
  const arcTransition = { duration: 1.05 + card.drift * 0.35, delay, times: [0, 0.42, 1], ease: ["easeOut", easeOutSoft] };
  return {
    initial: { x: originX, y: originY, rotate: 0, scale: 0.16, opacity: 0 },
    animate: {
      x: [originX, midX, pose.x],
      y: [originY, apexY, pose.y],
      rotate: [0, card.spin * 80, pose.rotate],
      scale: [0.16, 0.9, pose.scale],
      opacity: [0, 1, 1],
    },
    transition: { x: arcTransition, y: arcTransition, rotate: arcTransition, scale: arcTransition, opacity: { duration: 0.2, delay } },
  };
};

function PolaroidPhoto({ src, width, height, onAspect }) {
  const [isLoaded, setIsLoaded] = useState(false);
  return (
    <div className="polaroid-photo" style={{ width, height }}>
      <img
        src={src}
        alt=""
        decoding="async"
        draggable={false}
        className={isLoaded ? "is-loaded" : undefined}
        onLoad={(event) => {
          setIsLoaded(true);
          const { naturalWidth, naturalHeight } = event.currentTarget;
          if (naturalWidth && naturalHeight) onAspect(naturalWidth / naturalHeight);
        }}
      />
    </div>
  );
}

export default function PhotoCollage({ month, entranceStyle, origin, viewport, onOpenPhoto, onClose }) {
  const isPresent = useIsPresent();
  const prefersReducedMotion = useReducedMotion();
  const photoCount = month.photos.length;
  const [isSettled, setIsSettled] = useState(false);
  const [isInteractive, setIsInteractive] = useState(false);
  const [initialAspects] = useState(() => month.photos.map((url) => getCachedAspect(url) || defaultAspect));
  const [loadedAspects, setLoadedAspects] = useState(initialAspects);
  const aspects = isSettled ? loadedAspects : initialAspects;
  const area = useMemo(() => collageArea(viewport), [viewport]);
  const cards = useMemo(() => computeCollageLayout(aspects, area, month.monthNumber), [aspects, area, month.monthNumber]);

  useEffect(() => {
    const timer = setTimeout(() => setIsInteractive(true), interactiveDelayMilliseconds);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const seconds = prefersReducedMotion ? 0.8 : entranceSeconds[entranceStyle](photoCount);
    const timer = setTimeout(() => setIsSettled(true), seconds * 1000 + 120);
    return () => clearTimeout(timer);
  }, [entranceStyle, photoCount, prefersReducedMotion]);

  const recordAspect = (index, aspect) => {
    setLoadedAspects((current) =>
      Math.abs(current[index] - aspect) < 0.01 ? current : current.map((value, position) => (position === index ? aspect : value))
    );
  };

  return (
    <motion.div
      className="collage"
      style={{ pointerEvents: isPresent ? "auto" : "none" }}
      onClick={() => {
        if (isSettled) onClose();
      }}
    >
      <motion.div
        className="collage-veil"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.4, delay: 0.5 } }}
        transition={{ duration: 0.5 }}
      />
      <motion.p
        className="collage-label"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10, transition: { duration: 0.25 } }}
        transition={{ duration: 0.6, delay: 0.35 }}
      >
        {monthLabel(month.monthNumber)}
      </motion.p>

      {cards.map((card, index) => {
        const pose = {
          x: card.centerX - card.width / 2,
          y: card.centerY - card.height / 2,
          rotate: card.rotation,
          scale: card.scale,
          opacity: 1,
        };
        const entrance = prefersReducedMotion
          ? { initial: { ...pose, opacity: 0 }, animate: pose, transition: { duration: 0.4, delay: index * 0.025 } }
          : buildEntrance({ style: entranceStyle, card, index, count: photoCount, origin, area, viewport, pose });
        const gatherDelay = (photoCount - 1 - index) * (0.32 / Math.max(1, photoCount));
        return (
          <motion.button
            type="button"
            key={`${month.photos[index]}-${index}`}
            className="polaroid"
            style={{
              width: card.width,
              height: card.height,
              zIndex: card.zIndex,
              padding: `${card.framePadding}px ${card.framePadding}px 0`,
            }}
            initial={entrance.initial}
            animate={isSettled ? pose : entrance.animate}
            transition={isSettled ? { type: "spring", stiffness: 140, damping: 22 } : entrance.transition}
            exit={
              prefersReducedMotion
                ? { opacity: 0, transition: { duration: 0.3 } }
                : {
                    x: origin.x - card.width / 2,
                    y: origin.y - card.height / 2,
                    rotate: card.spin * 50,
                    scale: 0.1,
                    opacity: [1, 1, 0],
                    transition: {
                      duration: 0.6,
                      delay: gatherDelay,
                      ease: [0.55, 0, 0.7, 0.25],
                      opacity: { duration: 0.6, delay: gatherDelay, times: [0, 0.75, 1] },
                    },
                  }
            }
            whileHover={isSettled ? { scale: card.scale * 1.06, rotate: 0, transition: { duration: 0.25 } } : undefined}
            whileTap={{ scale: card.scale * 0.97 }}
            onClick={(event) => {
              event.stopPropagation();
              if (isInteractive) onOpenPhoto(index);
            }}
            aria-label={`open photo ${index + 1} of ${photoCount}`}
          >
            {card.hasTape && (
              <div className="polaroid-tape" style={{ transform: `translateX(-50%) rotate(${card.tapeRotation}deg)` }} />
            )}
            <PolaroidPhoto
              src={month.photos[index]}
              width={card.photoWidth}
              height={card.photoHeight}
              onAspect={(aspect) => recordAspect(index, aspect)}
            />
          </motion.button>
        );
      })}

      <motion.button
        type="button"
        className="love-pill collage-close"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 12, transition: { duration: 0.2 } }}
        transition={{ duration: 0.5, delay: 0.6 }}
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
      >
        tap for more my love
      </motion.button>
    </motion.div>
  );
}
